﻿import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Icon, Menu, Pagination, Select, DropdownProps } from 'semantic-ui-react';
import styled from 'styled-components';
import { FileTypes, getFileTypeIcon } from 'common/constants/filetypes';
import { getFileSizeFormatted, getFileLabelFromType } from 'areas/settings/repository-management/utils/LabelUtils';
import MobileKnowledgeBaseContentShareModal from './MobileKnowledgeBaseContentShareModal';
import { MobilePageContent, MobilePageSection, Title, SectionHeader, SectionDescription } from '@/mobile/layouts/common/MobilePageStyles';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { MobileSpinner } from '@/mobile/layouts/common/MobilePageStyles';
import { ResourceCategoryRead, useGetFileUploadsQuery, useGetResourceCategoriesQuery } from 'areas/settings/repository-management/api/repositoryApi';
import { RepositoryFileRead } from '../../../store/generated/admin/repository-files';
import { AppConfig } from '../../../Config';
import { authenticatedFetch } from 'common/utils/authFetch';
import { isYouTubeWatchUrl, openResourceLink } from 'common/utils/resourceLinks';
import { useSelector } from 'react-redux';
import { getCurrentOrganizationId } from 'common/selectors/auth';

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 5px;
  border: 1px solid #d9d9d9;
  background-color: #fff;
  font-size: 14px;
  color: #111;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #5f7fff;
    box-shadow: 0 0 0 3px rgba(95, 127, 255, 0.16);
  }
`;

const FilterSelect = styled(Select)`
  &&& {
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  &&& .text {
    line-height: 1.2;
    padding-top: 0;
    padding-bottom: 0;
  }

  &&& .menu {
    max-height: none !important;
    overflow-y: visible !important;
  }
`;

const MobileResourceListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [shareResource, setShareResource] = useState<RepositoryFileRead | null>(null);
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [activePage, setActivePage] = useState(1);

  const ALL_FILE_TYPES = '-1';
  const [typeFilter, setTypeFilter] = useState<string>(ALL_FILE_TYPES);
  const [searchText, setSearchText] = useState('');

  const selectedCategory = searchParams.get('category');
  const { data: categoryData } = useGetResourceCategoriesQuery();

  const rawCategoryData = categoryData as unknown;
  const categories: ResourceCategoryRead[] = Array.isArray(rawCategoryData)
    ? (rawCategoryData as ResourceCategoryRead[])
    : Array.isArray((rawCategoryData as { results?: ResourceCategoryRead[] })?.results)
      ? (rawCategoryData as { results: ResourceCategoryRead[] }).results
      : [];

  const selectedCategoryDetails = selectedCategory
    ? categories.find(category => category.slug === selectedCategory)
    : undefined;
  const selectedCategoryId = selectedCategoryDetails?.id;

  const { data, isLoading, error } = useGetFileUploadsQuery(
    selectedCategoryId ? { category_id: selectedCategoryId } : undefined
  );

  useEffect(() => {
    setActivePage(1);
  }, [typeFilter, searchText, selectedCategory]);

  const rawResourceData = data as unknown;
  const resources: RepositoryFileRead[] = Array.isArray(rawResourceData)
    ? (rawResourceData as RepositoryFileRead[])
    : Array.isArray((rawResourceData as { results?: RepositoryFileRead[] })?.results)
      ? (rawResourceData as { results: RepositoryFileRead[] }).results
      : [];

  const selectedCategoryName = selectedCategoryDetails?.name;
  const selectedCategoryIcon = selectedCategoryDetails?.icon;
  const selectedCategoryIconColor = selectedCategoryDetails?.color || '#5f70ff';

  const formatSlug = (slug: string) =>
    slug
      .split(/[-_]/)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const formatUrlPreview = (url?: string) => {
    if (!url) return '';
    try {
      const parsed = new URL(url);
      const pathname = parsed.pathname === '/' ? '' : parsed.pathname.split('/').slice(0, 3).join('/');
      return `${parsed.host}${pathname}...`;
    } catch {
      return url;
    }
  };

  const pageTitle = selectedCategory
    ? selectedCategoryName || formatSlug(selectedCategory)
    : 'Knowledge Base';

  const currentOrganizationId = useSelector(getCurrentOrganizationId);

  const recordLinkOpen = async (url: string, resourceName?: string, repositoryFileId?: number) => {
    if (!currentOrganizationId) {
      return;
    }

    const apiUrl = AppConfig.api.apiUrl || '';
    const endpoint = `${apiUrl}/api/portal/v1/assessments/leaderboard-events/link-open/`;

    try {
      await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-NULOGIC-ORGANIZATION-ID': String(currentOrganizationId),
        },
        body: JSON.stringify({
          url,
          resource_name: resourceName,
          repository_file_id: repositoryFileId,
        }),
      });
    } catch (error) {
      console.warn('Failed to record link open leaderboard event', error);
    }
  };

  const isYouTubeResource = (resource: RepositoryFileRead) =>
    resource.type === FileTypes.URL && isYouTubeWatchUrl(resource.object_key ?? undefined);

  const renderPrimaryIcon = (resource: RepositoryFileRead) => {
    const iconType = isYouTubeResource(resource)
      ? FileTypes.MP4
      : (resource.type ?? FileTypes.INVALID_FILE_TYPE);

    return getFileTypeIcon(iconType, { size: 'big' });
  };

  const renderMetaText = (resource: RepositoryFileRead) => {
    if (isYouTubeResource(resource)) {
      return (
        <YouTubeMetaInline data-testid="youtube-meta-label">
          <Icon name="youtube" color="red" />
          <span>YouTube</span>
        </YouTubeMetaInline>
      );
    }

    return (
      <MetaText title={resource.object_key || undefined}>
        {resource.type === FileTypes.URL
          ? formatUrlPreview(resource.object_key)
          : getFileSizeFormatted(resource.size ?? 0)}
      </MetaText>
    );
  };

  const filteredResources = (selectedCategoryId
    ? resources.filter(resource =>
      resource.category_ids?.includes(selectedCategoryId)
    )
    : resources
  )
    .filter(resource => {
      if (typeFilter === ALL_FILE_TYPES) {
        return true;
      }

      if (typeFilter === FileTypes.MP4.toString()) {
        return resource.type === FileTypes.MP4 || isYouTubeResource(resource);
      }

      if (typeFilter === FileTypes.URL.toString()) {
        return resource.type === FileTypes.URL && !isYouTubeResource(resource);
      }

      return resource.type?.toString() === typeFilter;
    })
    .filter(resource => {
      const query = searchText.trim().toLowerCase();
      if (!query) return true;
      const text = `${resource.name ?? ''} ${resource.description ?? ''}`.toLowerCase();
      return text.includes(query);
    });
  const itemsPerPage = 10;
  const paginatedResources = filteredResources.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage);
  const emptyMessage = selectedCategory
    ? 'No resources found for this category.'
    : 'No resources found.';

  const handleResourceClick = (resource: RepositoryFileRead) => {
    openResourceLink(resource, {
      recordLinkOpen: (url, resourceName, repositoryFileId) => {
        void recordLinkOpen(url, resourceName, repositoryFileId);
      },
      openInApp: () => {
        navigate(`/app/mobile/knowledge-base/resource/${resource.id}`, {
          state: { title: resource.name },
        });
      },
      openInNewTab: url => window.open(url, '_blank', 'noopener'),
    });
  };

  const handleShareClick = (resource: RepositoryFileRead) => {
    setShareResource(resource);
    setShareModalOpen(true);
  };

  const handleShareClose = () => {
    setShareResource(null);
    setShareModalOpen(false);
  };

  const handlePageChange = (_: any, { activePage: page }: any) => {
    setActivePage(page);
  };

  return (
    <MobilePageContent>
      <TopHeader>
        <SectionHeader>
          {selectedCategoryIcon && (
            <TitleIconWrapper>
              <Icon name={selectedCategoryIcon as any} />
            </TitleIconWrapper>
          )}
          <HeaderText>
            <Title>{pageTitle}</Title>
          </HeaderText>
        </SectionHeader>
        <SectionDescription>
          Browse available knowledge base resources.
        </SectionDescription>
      </TopHeader>
      <MobilePageSection padtop>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <SearchInput
            id="resource-table-search"
            type="search"
            placeholder="Search by name or description"
            value={searchText}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchText(event.target.value)}
            aria-label="Search resources"
            spellCheck={false}
            style={{ flex: '1 1 320px', minWidth: '260px' }}
          />
          <FilterSelect
            placeholder="Filter by Type"
            options={[
              { key: 'all', value: ALL_FILE_TYPES, text: 'All Files' },
              { key: 'pdf', value: FileTypes.PDF.toString(), text: 'Documents' },
              { key: 'mp4', value: FileTypes.MP4.toString(), text: 'Videos' },
              { key: 'url', value: FileTypes.URL.toString(), text: 'Links' },
            ]}
            value={typeFilter}
            onChange={(_e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => setTypeFilter(data.value as string)}
          />
        </div>
        {isLoading ? (
          <MobileSpinner />
        ) : error ? (
          <div>Unable to load resources. Please try again later.</div>
        ) : (
          <>
            <DesktopTableWrapper>
              <Table selectable>
                <TableHeader>
                  <Table.Row>
                    <HeaderCell width={5}>Name</HeaderCell>
                    <HeaderCell width={7}>Description</HeaderCell>
                    <HeaderCell width={1}>Type</HeaderCell>
                    <HeaderCell width={1}>Size</HeaderCell>
                    <HeaderCell width={1}></HeaderCell>
                  </Table.Row>
                </TableHeader>
                <TableBody>
                  {filteredResources.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan="5" style={{ textAlign: 'center', padding: '2rem 2rem 2rem 3rem', color: '#666' }}>
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedResources.map(resource => (
                      <TableRow
                        key={resource.id}
                        $selectable
                        height="70px"
                        onClick={() => handleResourceClick(resource)}
                      >
                        <TableCell $firstcol style={{ verticalAlign: 'top' }} fontSize="14px">
                          <ResourceRowContent>
                            {renderPrimaryIcon(resource)}
                            <ResourceNameBlock>
                              <ResourceTitleRow>
                                {resource.name}
                              </ResourceTitleRow>
                              {isYouTubeResource(resource) ? (
                                <YouTubeMetaInline data-testid="youtube-meta-label">
                                  <Icon name="youtube" color="red" />
                                  <span>YouTube</span>
                                </YouTubeMetaInline>
                              ) : (
                                <ResourceMetaText title={resource.object_key || undefined}>
                                  {resource.type === FileTypes.URL
                                    ? formatUrlPreview(resource.object_key)
                                    : resource.object_key?.length > 40
                                      ? `${resource.object_key.slice(0, 40)}...${resource.object_key.slice(-4)}`
                                      : resource.object_key}
                                </ResourceMetaText>
                              )}
                            </ResourceNameBlock>
                          </ResourceRowContent>
                        </TableCell>
                        <TableCell style={{ verticalAlign: 'top' }}>
                          <ResourceDescriptionText title={resource.description || 'No description'}>
                            {resource?.description
                              ? resource.description.length > 200
                                ? `${resource.description.slice(0, 200)}...`
                                : resource.description
                              : 'No description'}
                          </ResourceDescriptionText>
                        </TableCell>
                        <TableCell>{getFileLabelFromType(resource.type ?? -1)}</TableCell>
                        <TableCell>{resource.type === FileTypes.URL ? 'Link' : getFileSizeFormatted(resource.size ?? 0)}</TableCell>
                        <TableCell style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                          <Button
                            icon
                            basic
                            title="Share Resource"
                            aria-label="Share Resource"
                            style={{ width: 36, height: 36, minWidth: 36, padding: 0 }}
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                              event.stopPropagation();
                              handleShareClick(resource);
                            }}
                          >
                            <Icon name="share" size="large" />
                          </Button>
                          <Button
                            icon
                            basic
                            title="More Information"
                            aria-label="More Information"
                            style={{ width: 36, height: 36, minWidth: 36, padding: 0 }}
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                              event.stopPropagation();
                              navigate(`/app/mobile/knowledge-base/resource/${resource.id}/details`, {
                                state: { title: resource.name },
                              });
                            }}
                          >
                            <Icon name="info circle" size="large" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </DesktopTableWrapper>

            <MobileCardList>
              {filteredResources.length === 0 ? (
                <EmptyMobileMessage>{emptyMessage}</EmptyMobileMessage>
              ) : (
                paginatedResources.map(resource => (
                  <MobileCard key={resource.id} onClick={() => handleResourceClick(resource)}>
                    <MobileCardHeader>
                      {renderPrimaryIcon(resource)}
                      <MobileCardTitle>
                        <ResourceTitleRow>{resource.name}</ResourceTitleRow>
                      </MobileCardTitle>
                    </MobileCardHeader>
                    <MobileCardBody>
                      <CardRow>
                        <CardValue>
                          {resource?.description
                            ? resource.description.length > 120
                              ? `${resource.description.slice(0, 120)}...`
                              : resource.description
                            : 'No description'}
                        </CardValue>
                      </CardRow>
                      <MobileCardFooter>
                        <FileMeta>
                          {renderMetaText(resource)}
                        </FileMeta>
                        <MobileCardActions>
                          <IconButton
                            title="Share Resource"
                            aria-label="Share Resource"
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                              event.stopPropagation();
                              handleShareClick(resource);
                            }}
                          >
                            <Icon name="share" size="large" />
                          </IconButton>
                          <IconButton
                            title="More Information"
                            aria-label="More Information"
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                              event.stopPropagation();
                              navigate(`/app/mobile/knowledge-base/resource/${resource.id}/details`, {
                                state: { title: resource.name },
                              });
                            }}
                          >
                            <Icon name="info circle" size="large" />
                          </IconButton>
                        </MobileCardActions>
                      </MobileCardFooter>
                    </MobileCardBody>
                  </MobileCard>
                ))
              )}
            </MobileCardList>

            {filteredResources.length > itemsPerPage && (
              <Menu pagination>
                <Pagination
                  totalPages={Math.ceil(filteredResources.length / itemsPerPage)}
                  activePage={activePage}
                  onPageChange={handlePageChange}
                  boundaryRange={1}
                  size="mini"
                  firstItem={null}
                  lastItem={null}
                />
              </Menu>
            )}
          </>
        )}
      </MobilePageSection>

      <MobileKnowledgeBaseContentShareModal
        fileId={shareResource?.id ?? 0}
        fileName={shareResource?.name ?? ''}
        isOpen={isShareModalOpen}
        setModalOpen={setShareModalOpen}
      />
    </MobilePageContent>
  );
};

const ResourceRowContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ResourceNameBlock = styled.div``;

const ResourceTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ResourceMetaText = styled.div`
  font-size: 0.85em;
  color: grey;
  margin-top: 0.25rem;
`;

const ResourceDescriptionText = styled.div`
  font-size: 0.95em;
  color: grey;
  margin-top: 2px;
  padding-right: 1rem;
`;

const DesktopTableWrapper = styled.div`
  @media (max-width: 900px) {
    display: none;
  }
`;

const MobileCardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 900px) {
    display: flex;
  }
`;

const EmptyMobileMessage = styled.div`
  padding: 1.5rem;
  color: #666;
  text-align: center;
`;

const MobileCard = styled.div`
  border: 1px solid #d4d4d5;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 12px rgba(0, 0, 0, 0.12);
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  font-weight: 600;
  border-bottom: 1px solid #eff2f7;
`;

const MobileCardTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const MobileCardBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CardRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const CardValue = styled.div`
  width: 100%;
  font-size: 14px;
  color: #334155;
  white-space: pre-wrap;
`;

const MobileCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 12px;
`;

const FileMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  font-size: 0.95rem;
`;

const MetaText = styled.span`
  white-space: nowrap;
`;

const YouTubeMetaInline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #6b7280;
  font-size: 0.95rem;
  line-height: 1;

  span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  i.icon {
    margin: 0 !important;
    color: #db2828;
    line-height: 1 !important;
    vertical-align: middle !important;
  }
`;

const MetaSeparator = styled.span`
  color: #cbd5e1;
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: 12px;
`;

const IconButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  padding: 0;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #d4d4d5;
  color: #1f2937;
  cursor: pointer;

  &:hover {
    background: #eef2ff;
  }
`;

const TopHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;


const TitleIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #5f70ff;
  color: #ffffff;
  font-size: 18px;
  flex-shrink: 0;

  svg,
  i {
    position: relative;
    top: -3px;
    left: 2px;
  }
`;

const HeaderText = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;



export default MobileResourceListPage;
